// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable no-barrel-files/no-barrel-files */
import { type AggRequest, type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import {
	type SearchUseCaseIdWireVariables,
	toSearchUseCaseIdWireVariables,
} from '../../../common/utils/search-use-case-id';
import { generateHashFromVariables } from '../../../utils/caching/common/key-generator';
import { inMemoryCache } from '../../../utils/caching/in-memory';
import { type NetworkCallbacks } from '../../../utils/caching/persist-refresh';
import { withNetworkCallback } from '../../../utils/network-task-tracker';

export {
	getCombinedSearchConfig,
	seedCombinedConfigCacheEntry,
	updateCombinedConfigCacheEntry,
	type CombinedSearchConfig,
	type CombinedSearchConfigProps,
	type SearchCombinedConfigPayload,
	type SearchCombinedFirstPartyProduct,
	type SearchCombinedSiteMetadata,
	type SearchCombinedSiteProductInfo,
	type SearchCombinedThirdPartyProduct,
} from './config/index';

import { dcSpaceFilterOptionsQueryGenerator } from './dc-space-filter-options-query-generator';
import {
	confluenceDcContentFilterOptionsOperationName,
	confluenceDcContentFilterOptionsQueryGenerator,
	type ConfluenceDcContentFilterOptionsQueryVariables,
	confluenceDcPagesOperationName,
	confluenceDcPagesQueryGenerator,
	type ConfluenceDcPagesQueryVariables,
	confluenceDcSpaceFilterOptionsOperationName,
	type DcSpaceFilterOptionsQueryVariables,
	jiraDcProjectFilterOptionsOperationName,
	queryGenerator,
	resultsCountOperationName,
	resultsCountQueryGenerator,
	type ResultsCountQueryVariables,
	SEARCH_DIALOG_OPERATION_NAME,
	SEARCH_PAGE_OPERATION_NAME,
	searchLinkedSiteQueryGenerator,
	searchLinkedSitesOperationName,
	type SearchLinkedSitesQueryVariables,
	type SearchPageQueryVariables,
	spaceARIEntitiesOperationName,
	spaceARIEntitiesQueryGenerator,
	type SpaceARIEntitiesQueryVariables,
	documentARIEntitiesOperationName,
	documentARIEntitiesQueryGenerator,
	type DocumentARIEntitiesQueryVariables,
	thirdPartyRepositoryFilterOptionsOperationName,
	thirdPartyRepositoryFilterOptionsQueryGenerator,
	type ThirdPartyRepositoryFilterOptionsQueryVariables,
	thirdPartySpaceFilterOptionsOperationName,
	thirdPartySpaceFilterOptionsQueryGenerator,
	type ThirdPartySpaceFilterOptionsQueryVariables,
} from './query-generator';
import {
	type ConfluenceDCFilterOptionsData,
	type ConfluenceDCPagesData,
	type DCSpaceFilterOptionsData,
	type SearchLinkedSitesData,
	type SearchPageData,
	type SpaceARIEntitiesData,
	type DocumentARIEntitiesData,
	type ThirdPartyRepositoryFilterOptionsData,
} from './types';

type BaseQueryProps<T> = Pick<AggRequest<T>, 'aggAbsoluteUrl' | 'variables' | 'headers'> & {
	originalQuery?: string;
};

type WireVariables<T extends { useCaseId: unknown }> = Omit<T, 'useCaseId'> &
	SearchUseCaseIdWireVariables;

type SearchPageWireVariables = WireVariables<SearchPageQueryVariables>;
type ResultsCountWireVariables = WireVariables<ResultsCountQueryVariables>;
type DcSpaceFilterOptionsWireVariables = WireVariables<DcSpaceFilterOptionsQueryVariables>;
type ConfluenceDcContentFilterOptionsWireVariables =
	WireVariables<ConfluenceDcContentFilterOptionsQueryVariables>;
type ThirdPartySpaceFilterOptionsWireVariables =
	WireVariables<ThirdPartySpaceFilterOptionsQueryVariables>;
type ThirdPartyRepositoryFilterOptionsWireVariables =
	WireVariables<ThirdPartyRepositoryFilterOptionsQueryVariables>;

export type SearchPageQueryProps = BaseQueryProps<SearchPageQueryVariables>;
export type ResultsCountQueryProps = BaseQueryProps<ResultsCountQueryVariables>;
export type DcSpaceFilterOptionsQueryProps = BaseQueryProps<DcSpaceFilterOptionsQueryVariables>;
export type SpaceARIEntitiesQueryProps = BaseQueryProps<{ ids: string[] }>;
export type DocumentARIEntitiesQueryProps = BaseQueryProps<{ ids: string[] }>;
export type SearchLinkedSitesQueryProps = BaseQueryProps<{ id: string }>;
export type ConfluenceDcContentFilterOptionsQueryProps =
	BaseQueryProps<ConfluenceDcContentFilterOptionsQueryVariables>;
export type ConfluenceDcPagesQueryProps = BaseQueryProps<ConfluenceDcPagesQueryVariables>;
type SearchResultsQueryProps = SearchPageQueryProps & {
	useCache: boolean;
	operationName: string;
	serveUserShardUI?: boolean;
	networkCallbacks?: NetworkCallbacks;
};

export const searchPageQueryCache: {
	get: (key: string) => Promise<AggResponse<SearchPageData>> | undefined;
	set: (key: string, result: Promise<AggResponse<SearchPageData>>) => void;
	has: (key: string) => boolean;
	clear: (key?: string) => void;
	inMemoryDecorator: (
		key: string,
		supplier: () => Promise<AggResponse<SearchPageData>>,
		expireWhen?: ((result: AggResponse<SearchPageData>) => boolean) | undefined,
	) => Promise<AggResponse<SearchPageData>>;
} = inMemoryCache<AggResponse<SearchPageData>>();

export const cache: {
	get: (key: string) => Promise<AggResponse<SearchPageData>> | undefined;
	set: (key: string, result: Promise<AggResponse<SearchPageData>>) => void;
	has: (key: string) => boolean;
	clear: (key?: string) => void;
	inMemoryDecorator: (
		key: string,
		supplier: () => Promise<AggResponse<SearchPageData>>,
		expireWhen?: ((result: AggResponse<SearchPageData>) => boolean) | undefined,
	) => Promise<AggResponse<SearchPageData>>;
} = searchPageQueryCache;

export const generateCacheKey: (
	variables: SearchPageQueryVariables | ResultsCountQueryVariables,
	originalQuery?: string,
) => string = (
	variables: SearchPageQueryVariables | ResultsCountQueryVariables,
	originalQuery?: string,
): string => {
	/**
	 * Results once fetched will be used for upto 15 minutes from an in memory cache.
	 * 1. Generate key based on query, entities, filters
	 * 2. Serve from cache if present without resetting the expiration, evict if results are expired
	 * 3. If not present or expired then invoke the api
	 * 4. If API is invoked then store response in the cache with expiration and return back the response
	 */
	const keyObj = {
		query: variables.query,
		entities: variables.entities,
		commonFilters: variables.commonFilters,
		confluenceFilters: variables.confluenceFilters,
		jiraFilters: variables.jiraFilters,
		mercuryFilters: variables.mercuryFilters,
		compassFilters: variables.compassFilters,
		assetsFilters: variables.assetsFilters,
		thirdPartyFilters: variables.thirdPartyFilters,
		loomFilters: 'loomFilters' in variables ? variables.loomFilters : undefined,
		cloudIdARI: variables.cloudIdARI,
		sortField: variables.sort.at(0)?.field ?? '',
		sortOrder: variables.sort.at(0)?.order ?? '',
		after: variables.after,
		originalQuery,
	};

	return generateHashFromVariables(keyObj);
};

// Expire the cache if there are errors or edges are empty
const expireWhen = (result: AggResponse<SearchPageData>) => {
	const { data, errors } = result;
	if (!data || !data.search || (errors && errors.length > 0)) {
		return true;
	}
	return data.search?.results.edges.length === 0;
};

const searchQuery = ({
	variables,
	aggAbsoluteUrl,
	originalQuery,
	useCache,
	operationName,
	serveUserShardUI = false,
	headers,
	networkCallbacks,
}: SearchResultsQueryProps) => {
	// Default experience is search-page
	variables.experience = variables.experience || 'search-page';

	// Default skip3pEntityHydration is false
	variables.skip3pEntityHydration =
		variables.skip3pEntityHydration || variables.skipFieldsFor3pInQuickFind || false;

	// Disable GitHub hydration when skip3pEntityHydration is false to avoid duplication
	variables.includeGithubEntityHydration = !variables.skip3pEntityHydration
		? false
		: variables.includeGithubEntityHydration || false;

	variables.includeLoomVideoMetadata = variables.includeLoomVideoMetadata || false;

	variables.includeSpaceSubtype = variables.includeSpaceSubtype || false;

	variables.skipFieldsFor3pInQuickFind = variables.skipFieldsFor3pInQuickFind || false;

	// Since the default experience is search-page, default experience is to include social signals
	variables.includeSocialSignals =
		variables.includeSocialSignals === undefined ? true : variables.includeSocialSignals;

	variables.includeJiraIssueType = variables.includeJiraIssueType || false;

	variables.includeJiraBoardContainer = variables.includeJiraBoardContainer || false; // Used to include the container field

	// rovo_qf_jira_fast_hydration: gates the lighter value-field Jira issue hydration via
	// @include/@skip in the query. Defaults off so the query is unchanged unless a caller opts in.
	variables.includeFastJiraHydration = variables.includeFastJiraHydration || false;

	// Option to hydrate the confluenceEntity field
	variables.includeConfluenceEntity =
		typeof variables.includeConfluenceEntity === 'boolean'
			? variables.includeConfluenceEntity
			: true;

	variables.includeAutocompleteSuggestions = variables.includeAutocompleteSuggestions || false;

	const graphQuery = queryGenerator(operationName, serveUserShardUI);
	const requestVariables = toSearchUseCaseIdWireVariables(variables);

	if (useCache) {
		return searchPageQueryCache.inMemoryDecorator(
			generateCacheKey(variables, originalQuery),
			withNetworkCallback(
				() =>
					fetchAgg<SearchPageWireVariables, SearchPageData>({
						variables: requestVariables,
						aggAbsoluteUrl,
						graphQuery,
						operationName,
						headers,
					}),
				networkCallbacks,
			),
			expireWhen,
		);
	} else {
		return withNetworkCallback(
			() =>
				fetchAgg<SearchPageWireVariables, SearchPageData>({
					variables: requestVariables,
					aggAbsoluteUrl,
					graphQuery,
					operationName,
					headers,
				}),
			networkCallbacks,
		)();
	}
};

export const searchPageQuery: ({
	variables,
	aggAbsoluteUrl,
	originalQuery,
	serveUserShardUI,
	headers,
}: SearchPageQueryProps & {
	serveUserShardUI?: boolean;
}) => Promise<AggResponse<SearchPageData>> = ({
	variables,
	aggAbsoluteUrl,
	originalQuery,
	serveUserShardUI = false,
	headers,
}: SearchPageQueryProps & { serveUserShardUI?: boolean }) => {
	return searchQuery({
		variables,
		aggAbsoluteUrl,
		originalQuery,
		useCache: true,
		operationName: SEARCH_PAGE_OPERATION_NAME,
		serveUserShardUI,
		headers,
	});
};

export const searchDialogQuery: ({
	variables,
	aggAbsoluteUrl,
	originalQuery,
	serveUserShardUI,
	headers,
	networkCallbacks,
	operationName,
}: SearchPageQueryProps & {
	serveUserShardUI?: boolean;
	networkCallbacks?: NetworkCallbacks;
	operationName?: string;
}) => Promise<AggResponse<SearchPageData>> = ({
	variables,
	aggAbsoluteUrl,
	originalQuery,
	serveUserShardUI = false,
	headers,
	networkCallbacks,
	operationName = SEARCH_DIALOG_OPERATION_NAME,
}: SearchPageQueryProps & {
	serveUserShardUI?: boolean;
	networkCallbacks?: NetworkCallbacks;
	operationName?: string;
}) => {
	return searchQuery({
		variables,
		aggAbsoluteUrl,
		originalQuery,
		useCache: false, // Bypassing cache to match previous Quick Find behavior
		operationName,
		serveUserShardUI,
		headers,
		networkCallbacks,
	});
};

export const resultsCountQuery: ({
	variables,
	aggAbsoluteUrl,
	originalQuery,
	headers,
}: ResultsCountQueryProps) => Promise<AggResponse<SearchPageData>> = ({
	variables,
	aggAbsoluteUrl,
	originalQuery,
	headers,
}: ResultsCountQueryProps) => {
	const graphQuery = resultsCountQueryGenerator();
	const requestVariables = toSearchUseCaseIdWireVariables(variables);

	return searchPageQueryCache.inMemoryDecorator(
		'count' + generateCacheKey(variables, originalQuery),
		() =>
			fetchAgg<ResultsCountWireVariables, SearchPageData>({
				variables: requestVariables,
				aggAbsoluteUrl,
				graphQuery,
				operationName: resultsCountOperationName,
				headers,
			}),
	);
};

export const jiraDcProjectFilterOptionsQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
}: DcSpaceFilterOptionsQueryProps) => Promise<AggResponse<DCSpaceFilterOptionsData>> = ({
	variables,
	aggAbsoluteUrl,
	headers,
}: DcSpaceFilterOptionsQueryProps) => {
	const graphQuery = dcSpaceFilterOptionsQueryGenerator(jiraDcProjectFilterOptionsOperationName);
	const requestVariables = toSearchUseCaseIdWireVariables(variables);

	return fetchAgg<DcSpaceFilterOptionsWireVariables, DCSpaceFilterOptionsData>({
		variables: requestVariables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: jiraDcProjectFilterOptionsOperationName,
		headers,
	});
};

export const spaceARIEntitiesQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
}: SpaceARIEntitiesQueryProps) => Promise<AggResponse<SpaceARIEntitiesData>> = ({
	variables,
	aggAbsoluteUrl,
	headers,
}: SpaceARIEntitiesQueryProps) => {
	const graphQuery = spaceARIEntitiesQueryGenerator();

	return fetchAgg<SpaceARIEntitiesQueryVariables, SpaceARIEntitiesData>({
		variables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: spaceARIEntitiesOperationName,
		headers,
	});
};

export const confluenceDcContentFilterOptionsQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
}: ConfluenceDcContentFilterOptionsQueryProps) => Promise<
	AggResponse<ConfluenceDCFilterOptionsData>
> = ({ variables, aggAbsoluteUrl, headers }: ConfluenceDcContentFilterOptionsQueryProps) => {
	const graphQuery = confluenceDcContentFilterOptionsQueryGenerator();
	const requestVariables = toSearchUseCaseIdWireVariables(variables);

	return fetchAgg<ConfluenceDcContentFilterOptionsWireVariables, ConfluenceDCFilterOptionsData>({
		variables: requestVariables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: confluenceDcContentFilterOptionsOperationName,
		headers,
	});
};

export const documentARIEntitiesQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
}: DocumentARIEntitiesQueryProps) => Promise<AggResponse<DocumentARIEntitiesData>> = ({
	variables,
	aggAbsoluteUrl,
	headers,
}: DocumentARIEntitiesQueryProps) => {
	const graphQuery = documentARIEntitiesQueryGenerator();

	return fetchAgg<DocumentARIEntitiesQueryVariables, DocumentARIEntitiesData>({
		variables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: documentARIEntitiesOperationName,
		headers,
	});
};

export const confluenceDcPagesQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
}: ConfluenceDcPagesQueryProps) => Promise<AggResponse<ConfluenceDCPagesData>> = ({
	variables,
	aggAbsoluteUrl,
	headers,
}: ConfluenceDcPagesQueryProps) => {
	const graphQuery = confluenceDcPagesQueryGenerator();

	return fetchAgg<ConfluenceDcPagesQueryVariables, ConfluenceDCPagesData>({
		variables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: confluenceDcPagesOperationName,
		headers,
	});
};

export const confluenceDcSpaceFilterOptionsQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
}: DcSpaceFilterOptionsQueryProps) => Promise<AggResponse<DCSpaceFilterOptionsData>> = ({
	variables,
	aggAbsoluteUrl,
	headers,
}: DcSpaceFilterOptionsQueryProps) => {
	const graphQuery = dcSpaceFilterOptionsQueryGenerator(
		confluenceDcSpaceFilterOptionsOperationName,
	);
	const requestVariables = toSearchUseCaseIdWireVariables({
		...variables,
		integration: 'ari:cloud:platform::integration/confluence-dc',
	});

	return fetchAgg<DcSpaceFilterOptionsWireVariables, DCSpaceFilterOptionsData>({
		variables: requestVariables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: confluenceDcSpaceFilterOptionsOperationName,
		headers,
	});
};

export type ThirdPartySpaceFilterOptionsQueryProps =
	BaseQueryProps<ThirdPartySpaceFilterOptionsQueryVariables>;

export const thirdPartySpaceFilterOptionsQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
}: ThirdPartySpaceFilterOptionsQueryProps) => Promise<AggResponse<DCSpaceFilterOptionsData>> = ({
	variables,
	aggAbsoluteUrl,
	headers,
}: ThirdPartySpaceFilterOptionsQueryProps) => {
	const graphQuery = thirdPartySpaceFilterOptionsQueryGenerator();
	const requestVariables = toSearchUseCaseIdWireVariables(variables);

	return fetchAgg<ThirdPartySpaceFilterOptionsWireVariables, DCSpaceFilterOptionsData>({
		variables: requestVariables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: thirdPartySpaceFilterOptionsOperationName,
		headers,
	});
};

export type ThirdPartyRepositoryFilterOptionsQueryProps =
	BaseQueryProps<ThirdPartyRepositoryFilterOptionsQueryVariables>;

export const thirdPartyRepositoryFilterOptionsQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
}: ThirdPartyRepositoryFilterOptionsQueryProps) => Promise<
	AggResponse<ThirdPartyRepositoryFilterOptionsData>
> = ({ variables, aggAbsoluteUrl, headers }: ThirdPartyRepositoryFilterOptionsQueryProps) => {
	const graphQuery = thirdPartyRepositoryFilterOptionsQueryGenerator();
	const requestVariables = toSearchUseCaseIdWireVariables(variables);

	return fetchAgg<
		ThirdPartyRepositoryFilterOptionsWireVariables,
		ThirdPartyRepositoryFilterOptionsData
	>({
		variables: requestVariables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: thirdPartyRepositoryFilterOptionsOperationName,
		headers,
	});
};

export const searchLinkedSitesQuery: ({
	variables,
	aggAbsoluteUrl,
	headers,
}: SearchLinkedSitesQueryProps) => Promise<AggResponse<SearchLinkedSitesData>> = ({
	variables,
	aggAbsoluteUrl,
	headers,
}: SearchLinkedSitesQueryProps) => {
	const graphQuery = searchLinkedSiteQueryGenerator();

	return fetchAgg<SearchLinkedSitesQueryVariables, SearchLinkedSitesData>({
		variables,
		aggAbsoluteUrl,
		graphQuery,
		operationName: searchLinkedSitesOperationName,
		headers,
	});
};
